import { Injectable, signal } from '@angular/core';
import { TicketCommand } from './printing.model';

/**
 * Puertos candidatos del micro «ticketera». DEBE coincidir con la lista del micro (`candidatos()` en main.go):
 * arranca en el base y, si está ocupado, salta al siguiente.
 */
export const TICKETERA_PORTS = [48757, 48758, 48759, 48760];
const PING_TIMEOUT_MS = 700;
const PRINT_TIMEOUT_MS = 30_000;

export type AgentState = 'idle' | 'searching' | 'connected' | 'not-found';

export interface AgentPrinters {
  usb: string[];
  ethernet: string;
}

/** Error al imprimir. `unreachable` = el micro no respondió (se vuelve a buscar el puerto en el próximo intento). */
export class TicketeraError extends Error {
  constructor(message: string, readonly unreachable: boolean) {
    super(message);
  }
}

/**
 * Cliente del micro «ticketera» (estándar de PartnerTech, ver INTEGRACION_APPS.md): binario local en la PC del cajero
 * que recibe comandos de dibujo y habla ESC/POS con la impresora por USB o Ethernet.
 *
 * - Se usa `http://127.0.0.1`, no `localhost`: es «potentially trustworthy» y una página HTTPS puede llamarlo.
 * - El puerto se descubre probando los candidatos (camino rápido: el último que respondió o el registrado).
 * - `fetch` nativo y no HttpClient: los interceptores del panel añadirían el token de sesión, que no debe salir hacia
 *   un servicio local.
 */
@Injectable({ providedIn: 'root' })
export class TicketeraAgentService {
  readonly state = signal<AgentState>('idle');
  readonly port = signal<number | null>(null);
  readonly version = signal<string | null>(null);
  readonly printers = signal<AgentPrinters | null>(null);

  /** Busca el micro: primero el puerto preferido o el último conocido, después la lista. null si no está. */
  async discover(preferredPort?: number | null): Promise<number | null> {
    const known = this.port();
    if (known && (await this.responds(known))) {
      this.state.set('connected');
      return known;
    }
    this.state.set('searching');
    const candidates = [preferredPort, ...TICKETERA_PORTS].filter(
      (p, i, all): p is number => typeof p === 'number' && p > 0 && all.indexOf(p) === i && p !== known);
    for (const port of candidates) {
      if (await this.responds(port)) {
        this.port.set(port);
        this.state.set('connected');
        return port;
      }
    }
    this.port.set(null);
    this.state.set('not-found');
    return null;
  }

  /** Versión e impresoras detectadas por el micro (USB instaladas / destino Ethernet configurado). */
  async refreshInfo(): Promise<void> {
    const port = this.port();
    if (!port) return;
    const [version, printers] = await Promise.all([
      this.getJson<{ version: string }>(port, '/version').catch(() => null),
      this.getJson<AgentPrinters>(port, '/printers').catch(() => null),
    ]);
    this.version.set(version?.version ?? null);
    this.printers.set(printers ? { usb: printers.usb ?? [], ethernet: printers.ethernet ?? '' } : null);
  }

  /** Página local del micro, donde se elige USB/Ethernet y la impresora (única fuente de verdad de esa config). */
  configUrl(): string | null {
    const port = this.port();
    return port ? `http://127.0.0.1:${port}/` : null;
  }

  /** Envía el ticket al micro. Lanza TicketeraError con el motivo que devolvió el micro (502 = impresora). */
  async print(commands: TicketCommand[], preferredPort?: number | null): Promise<void> {
    const port = await this.discover(preferredPort);
    if (!port) throw new TicketeraError('ticketera no disponible', true);
    let response: Response;
    try {
      response = await fetch(`http://127.0.0.1:${port}/print`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ commands }),
        signal: AbortSignal.timeout(PRINT_TIMEOUT_MS),
      });
    } catch (e) {
      this.port.set(null);
      this.state.set('not-found');
      throw new TicketeraError(e instanceof Error ? e.message : String(e), true);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({} as { error?: string }));
      throw new TicketeraError(body?.error ?? `HTTP ${response.status}`, false);
    }
  }

  /** ¿Responde NUESTRO micro en este puerto? La marca `service` evita confundirlo con otro servidor local. */
  private async responds(port: number): Promise<boolean> {
    try {
      const body = await this.getJson<{ service?: string }>(port, '/ping', PING_TIMEOUT_MS);
      return body?.service === 'ticketera';
    } catch {
      return false;
    }
  }

  private async getJson<T>(port: number, path: string, timeout = PING_TIMEOUT_MS * 2): Promise<T> {
    const response = await fetch(`http://127.0.0.1:${port}${path}`, { signal: AbortSignal.timeout(timeout) });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json() as Promise<T>;
  }
}
